import React from "react";
import { useLang, translateFr as tfr } from "@/i18n";
import { useNavigate } from "react-router-dom";
import { CalendarClock, MapPin, Video } from "lucide-react";
import DashboardSectionCard from "@/components/dashboard/DashboardSectionCard";
import DashboardCardAction from "@/components/dashboard/DashboardCardAction";
import { cn, formatTime } from "@/lib/utils";

const TYPE_META = {
  call: { label: "Appel", color: "bg-info" },
  meeting: { label: "Rendez-vous", color: "bg-info" },
  "rdv-club": { label: "RDV club", color: "bg-info" },
  match: { label: "Match", color: "bg-success" },
  video: { label: "Visio", color: "bg-info" },
  "follow-up": { label: tfr("Relance"), color: "bg-warning" },
  relance: { label: tfr("Relance"), color: "bg-warning" },
  deadline: { label: "Deadline", color: "bg-danger" },
  training: { label: tfr("Entraînement"), color: "bg-success" },
};

function dateLabel(iso) {
  const en = (() => { try { return localStorage.getItem("athlynx_lang") === "en"; } catch { return false; } })();
  const d = new Date(iso);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const dd = new Date(d); dd.setHours(0, 0, 0, 0);
  const diff = Math.round((dd - today) / 86400000);
  if (diff === 0) return en ? "Today" : "Aujourd'hui";
  if (diff === 1) return en ? "Tomorrow" : "Demain";
  return d.toLocaleDateString(en ? "en-US" : "fr-FR", { day: "numeric", month: "short" });
}

export default function UpcomingEventsCard({ events = [] }) {
  const { t } = useLang();
  const navigate = useNavigate();
  const list = events.slice(0, 5);

  return (
    <DashboardSectionCard
      title={t("dash.card.upcoming")}
      icon={CalendarClock}
      iconClass="text-muted-foreground"
      count={events.length}
      testid="card-upcoming-events"
      footer={
        <DashboardCardAction to="/calendar" testid="upcoming-see-all">
          {t("dash.card.seeAllEvents")}
        </DashboardCardAction>
      }
    >
      {list.length === 0 ? (
        <div className="flex-1 flex items-center justify-center py-10 text-xs text-muted-foreground" data-testid="upcoming-empty">
          {t("dash.card.noEvents")}
        </div>
      ) : (
        <ol className="flex-1 min-h-0 overflow-y-auto overscroll-contain space-y-1" data-testid="upcoming-list">
          {list.map((e) => {
            const meta = TYPE_META[e.type] || { label: e.type, color: "bg-info" };
            const metaLabel = TYPE_META[e.type] ? t(`event.${e.type}`) : meta.label;
            const isVideo = e.type === "video" || /visio/i.test(e.title || "");
            return (
              <li key={e.id}>
                <button
                  onClick={() => navigate("/calendar")}
                  className="w-full flex items-center gap-2.5 rounded-lg px-2 py-2 hover:bg-secondary/60 transition-colors text-left"
                  data-testid={`upcoming-event-${e.id}`}
                >
                  <div className="shrink-0 w-16 text-right">
                    <div className="text-[11px] font-medium text-foreground/80">{dateLabel(e.starts_at)}</div>
                    <div className="text-[11px] text-muted-foreground tabular-nums">{formatTime(e.starts_at)}</div>
                  </div>
                  <span className={cn("w-1 self-stretch rounded-full shrink-0", meta.color)} aria-hidden />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{e.title || metaLabel}</div>
                    <div className="text-[11px] text-muted-foreground truncate flex items-center gap-1">
                      <span className="uppercase tracking-wider text-[9px]">{metaLabel}</span>
                      {e.location && (
                        <span className="flex items-center gap-0.5 truncate">· <MapPin className="h-2.5 w-2.5" /> {e.location}</span>
                      )}
                      {isVideo && <Video className="h-2.5 w-2.5 text-info" />}
                    </div>
                  </div>
                </button>
              </li>
            );
          })}
        </ol>
      )}
    </DashboardSectionCard>
  );
}
